import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Circle, AlertCircle, ArrowRight, Loader2, Rocket } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axiosInstance from '../../api/axios';

const ACTIONS = {
  insert_agb: { label: 'AGB-Entwurf einfügen', url: '/admin/launch-checklist/insert-draft/agb', confirm: 'AGB-Entwurf einfügen? Der bisherige Text wird gesichert und ersetzt.' },
  insert_privacy: { label: 'Datenschutz-Entwurf einfügen', url: '/admin/launch-checklist/insert-draft/datenschutz', confirm: 'Datenschutz-Entwurf einfügen? Der bisherige Text wird gesichert und ersetzt.' },
  insert_impressum: { label: 'Neues Impressum einfügen', url: '/admin/launch-checklist/insert-draft/impressum', confirm: 'Neues Impressum (DDG) mit deinen Firmendaten einfügen? Der bisherige Text wird gesichert.' },
  unify_emails: { label: 'E-Mail vereinheitlichen', url: '/admin/launch-checklist/unify-emails', confirm: 'Alle alten Kontakt-Adressen durch die E-Mail aus den Dokument-Einstellungen ersetzen?' },
  renew_coupon: { label: 'WILLKOMMEN10 anlegen', url: '/admin/launch-checklist/renew-coupon', confirm: 'Abgelaufene Gutscheine deaktivieren und WILLKOMMEN10 (10 %, 12 Monate) anlegen?' },
  fix_shipping: { label: 'Versandkosten angleichen', url: '/admin/launch-checklist/fix-shipping', confirm: 'Alle Versandmethoden auf die Versandkosten aus der Versandeinstellung setzen?' },
  migrate_images: { label: 'Bilder übernehmen', url: '/admin/launch-checklist/migrate-images', confirm: 'Externe Produktbilder jetzt in deinen eigenen Speicher kopieren?' },
};

const Item = ({ item, onToggle, onAction, busy }) => {
  const Icon = item.ok ? CheckCircle2 : item.type === 'auto' ? AlertCircle : Circle;
  const action = item.action && !item.ok ? ACTIONS[item.action] : null;
  return (
    <div className="flex gap-3 py-3 border-b border-[#EFE6DA] last:border-0" data-testid={`checklist-item-${item.key}`}>
      <button
        type="button"
        disabled={item.type === 'auto'}
        onClick={() => onToggle(item)}
        className={`mt-0.5 flex-shrink-0 ${item.type === 'manual' ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
        data-testid={`checklist-toggle-${item.key}`}
        aria-label={item.title}
      >
        <Icon className={`h-5 w-5 ${item.ok ? 'text-green-600' : item.type === 'auto' ? 'text-amber-500' : 'text-gray-400'}`} />
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${item.ok ? 'text-gray-500 line-through' : 'text-[#5D4E3C]'}`}>{item.title}</p>
        {!item.ok && item.hint && <p className="text-xs text-gray-600 mt-0.5">{item.hint}</p>}
        <div className="flex flex-wrap gap-2 mt-2">
          {action && (
            <Button size="sm" disabled={busy === item.key} onClick={() => onAction(item, action)} className="rounded-none h-7 text-xs bg-[#C4A484] hover:bg-[#B89968]" data-testid={`checklist-action-${item.key}`}>
              {busy === item.key && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {action.label}
            </Button>
          )}
          {item.link && !item.ok && (
            <Link to={item.link} className="inline-flex items-center text-xs text-[#8B7355] hover:underline" data-testid={`checklist-link-${item.key}`}>
              Zur Einstellung <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          )}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-wide text-gray-400 hidden sm:block">{item.type === 'auto' ? 'automatisch' : 'zum Abhaken'}</span>
    </div>
  );
};

export default function LaunchChecklist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axiosInstance.get('/admin/launch-checklist').then((r) => setData(r.data)).catch(() => toast.error('Checkliste konnte nicht geladen werden'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onToggle = async (item) => {
    await axiosInstance.put(`/admin/launch-checklist/${item.key}`, { done: !item.ok });
    load();
  };

  const pollImages = async () => {
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: st } = await axiosInstance.get('/admin/launch-checklist/migrate-images/status');
      if (st.status === 'done') {
        toast.success(`${st.products} Artikel aktualisiert${st.failed?.length ? ` – fehlgeschlagen: ${st.failed.join(', ')}` : ''}`);
        return;
      }
    }
  };

  const onAction = async (item, action) => {
    if (!window.confirm(action.confirm)) return;
    setBusy(item.key);
    try {
      const { data: res } = await axiosInstance.post(action.url);
      if (item.action === 'migrate_images') {
        toast.info('Bilder werden kopiert – das dauert einen Moment …');
        await pollImages();
      } else {
        toast.success(res.message || 'Erledigt');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <div className="p-8 text-sm text-gray-500">Lädt Checkliste …</div>;
  const groups = [...new Set(data.items.map((i) => i.group))];
  const pct = Math.round((data.done_count / data.total) * 100);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="launch-checklist-page">
      <div className="flex items-center gap-3 mb-2">
        <Rocket className="h-6 w-6 text-[#8B7355]" />
        <h1 className="text-2xl font-semibold text-[#5D4E3C]">Start-Checkliste</h1>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Automatische Punkte prüft der Shop selbst, die anderen hakst du ab. Bitte auf der <strong>Live-Seite</strong> abarbeiten.
      </p>
      <div className="mb-6" data-testid="checklist-progress">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>{data.done_count} von {data.total} erledigt</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 bg-[#EFE6DA]"><div className="h-2 bg-[#8B7355] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      {groups.map((g) => (
        <div key={g} className="bg-white border border-[#E8DED1] px-4 mb-4">
          <h2 className="text-xs uppercase tracking-widest text-[#8B7355] pt-3 pb-1">{g}</h2>
          {data.items.filter((i) => i.group === g).map((i) => (
            <Item key={i.key} item={i} onToggle={onToggle} onAction={onAction} busy={busy} />
          ))}
        </div>
      ))}
    </div>
  );
}
